/**
 * PersonaRoute — route guard that requires a tenant persona (Forwarder TMS Wave 1.3).
 *
 * Wraps a route element so forwarder-only routes are unreachable for tenants
 * that don't operate as freight forwarders — the frontend mirror of the backend
 * `require_forwarder_mode` dependency. While the persona is loading it shows a
 * skeleton; when the requirement isn't met `useRequirePersona` redirects home
 * (no NotFound page exists in this app, so home is the canonical "gone" target,
 * matching AdminRoute). Backend routes genuinely 404; the UI redirects.
 *
 *   <Route path="/shipments/consols" element={<PersonaRoute forwarder><ConsolsListPage /></PersonaRoute>} />
 */
import React from 'react';
import { useRequirePersona } from '../../shared/hooks/useRequirePersona';
import { PageSkeleton } from '../../shared/components/common';

interface PersonaRouteProps {
  /** Require the tenant to operate as a freight forwarder (sells_freight_services). */
  forwarder?: boolean;
  children: React.ReactNode;
}

export function PersonaRoute({ forwarder = false, children }: PersonaRouteProps) {
  const { isLoading, allowed } = useRequirePersona({ forwarder });

  if (isLoading) {
    return <PageSkeleton />;
  }
  if (!allowed) {
    // Redirect is in flight (useRequirePersona effect); render nothing meanwhile.
    return null;
  }
  return <>{children}</>;
}
